import { useEffect, useMemo, useState } from "react";
import { RefreshCw, Filter, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { api, formatApiErrorDetail } from "../../lib/api";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

const GEO_LEVELS = [
  { key: "country_id", label: "Country", apiPath: "countries", parent: null },
  { key: "state_id", label: "State", apiPath: "states", parent: "country_id" },
  { key: "district_id", label: "District", apiPath: "districts", parent: "state_id" },
  { key: "city_id", label: "City", apiPath: "cities", parent: "district_id" },
  { key: "taluk_id", label: "Taluk", apiPath: "taluks", parent: "city_id" },
  { key: "area_id", label: "Area", apiPath: "areas", parent: "taluk_id" },
];

const SENTINEL_ALL = "__all__"; // shadcn Select does not allow empty string values

/**
 * Shared report filters component.
 * Props:
 *  - userLabel: string label for the "User" filter
 *  - userApiPath: "/users" or "/members"
 *  - value: controlled filter state
 *  - onChange: (newFilters) => void
 *  - onApply: () => void
 *  - onReset: () => void
 *  - loading: boolean
 */
export default function ReportFilters({
  userLabel,
  userApiPath,
  value,
  onChange,
  onApply,
  onReset,
  loading,
}) {
  const [users, setUsers] = useState([]);
  const [geoData, setGeoData] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(userApiPath);
        setUsers(data || []);
      } catch (e) {
        toast.error(
          formatApiErrorDetail(e?.response?.data?.detail) ||
            `Failed to load ${userLabel.toLowerCase()} list`
        );
      }
    })();
  }, [userApiPath, userLabel]);

  // Load countries upfront, rest cascades
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/master/countries");
        setGeoData((prev) => ({ ...prev, countries: data || [] }));
      } catch {
        /* silent */
      }
    })();
  }, []);

  // When parent changes, load child list (cascading)
  const loadGeoLevel = async (level) => {
    const parentKey = level.parent;
    const parentVal = parentKey ? value[parentKey] : null;
    // Only load if parent is set (for non-root) — queries master API with parent filter
    try {
      const params = parentKey && parentVal ? { [parentKey]: parentVal } : undefined;
      const { data } = await api.get(`/master/${level.apiPath}`, { params });
      setGeoData((prev) => ({ ...prev, [level.apiPath]: data || [] }));
    } catch {
      setGeoData((prev) => ({ ...prev, [level.apiPath]: [] }));
    }
  };

  // Track cascades — load next level whenever its parent value changes
  useEffect(() => {
    GEO_LEVELS.forEach((lvl) => {
      if (lvl.key === "country_id") return; // already loaded at mount
      const parentVal = value[lvl.parent];
      if (parentVal) {
        loadGeoLevel(lvl);
      } else {
        // Clear the dropdown if parent cleared
        setGeoData((prev) => ({ ...prev, [lvl.apiPath]: [] }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    value.country_id,
    value.state_id,
    value.district_id,
    value.city_id,
    value.taluk_id,
  ]);

  const userOptions = useMemo(
    () =>
      users.map((u) => ({
        id: u.id,
        label:
          u.name ||
          u.email ||
          u.phone ||
          u.id.slice(0, 8),
        sub: u.email || u.phone || "",
      })),
    [users]
  );

  const onGeoChange = (key, v) => {
    // clear downstream ids whenever an ancestor changes
    const idx = GEO_LEVELS.findIndex((l) => l.key === key);
    const next = { ...value, [key]: v };
    for (let i = idx + 1; i < GEO_LEVELS.length; i += 1) {
      next[GEO_LEVELS[i].key] = "";
    }
    onChange(next);
  };

  return (
    <div
      className="bg-white border border-black/5 rounded-xl p-5 mb-5"
      data-testid="report-filters"
    >
      <div className="flex items-center gap-2 mb-4">
        <Filter className="h-4 w-4 text-[#525252]" />
        <span className="text-xs font-semibold uppercase tracking-wider text-[#525252]">
          Filters
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-[#525252] mb-1.5 block">
            From Date
          </Label>
          <Input
            type="date"
            value={value.from || ""}
            onChange={(e) => onChange({ ...value, from: e.target.value })}
            className="h-10 bg-white border-black/10"
            data-testid="filter-from"
          />
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-[#525252] mb-1.5 block">
            To Date
          </Label>
          <Input
            type="date"
            value={value.to || ""}
            onChange={(e) => onChange({ ...value, to: e.target.value })}
            className="h-10 bg-white border-black/10"
            data-testid="filter-to"
          />
        </div>

        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-[#525252] mb-1.5 block">
            {userLabel}
          </Label>
          <Select
            value={value.user_id || SENTINEL_ALL}
            onValueChange={(v) =>
              onChange({ ...value, user_id: v === SENTINEL_ALL ? "" : v })
            }
          >
            <SelectTrigger className="h-10 bg-white border-black/10" data-testid="filter-user">
              <SelectValue placeholder={`All ${userLabel.toLowerCase()}`} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={SENTINEL_ALL}>All {userLabel.toLowerCase()}</SelectItem>
              {userOptions.map((u) => (
                <SelectItem key={u.id} value={u.id}>
                  <div className="flex flex-col">
                    <span>{u.label}</span>
                    {u.sub ? (
                      <span className="text-[10px] text-[#A3A3A3]">{u.sub}</span>
                    ) : null}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {GEO_LEVELS.map((lvl) => {
          const options = geoData[lvl.apiPath] || [];
          const parentSelected = lvl.parent ? !!value[lvl.parent] : true;
          const disabled = !parentSelected;
          return (
            <div key={lvl.key}>
              <Label className="text-xs font-semibold uppercase tracking-wider text-[#525252] mb-1.5 block">
                {lvl.label}
              </Label>
              <Select
                value={value[lvl.key] || SENTINEL_ALL}
                onValueChange={(v) => onGeoChange(lvl.key, v === SENTINEL_ALL ? "" : v)}
                disabled={disabled}
              >
                <SelectTrigger
                  className="h-10 bg-white border-black/10"
                  data-testid={`filter-${lvl.key}`}
                >
                  <SelectValue
                    placeholder={
                      disabled
                        ? `Select ${GEO_LEVELS.find((g) => g.key === lvl.parent)?.label.toLowerCase()} first`
                        : `All ${lvl.label.toLowerCase()}s`
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={SENTINEL_ALL}>All {lvl.label.toLowerCase()}s</SelectItem>
                  {options.map((o) => (
                    <SelectItem key={o.id} value={o.id}>
                      {o.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          );
        })}
      </div>

      <div className="mt-5 flex items-center justify-end gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={onReset}
          className="h-10 border-black/10"
          data-testid="filter-reset"
        >
          <X className="h-4 w-4 mr-1.5" />
          Reset
        </Button>
        <Button
          type="button"
          onClick={onApply}
          disabled={loading}
          className="h-10 bg-[#F50E02] hover:bg-[#D80C02] text-white"
          data-testid="filter-apply"
        >
          {loading ? (
            <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4 mr-1.5" />
          )}
          Apply
        </Button>
      </div>
    </div>
  );
}

export const EMPTY_FILTERS = {
  from: "",
  to: "",
  user_id: "",
  country_id: "",
  state_id: "",
  district_id: "",
  city_id: "",
  taluk_id: "",
  area_id: "",
};
